import React, { forwardRef, useEffect, useState } from "react";

import Input from "@mui/material/Input";
import type { SxProps } from "@mui/material/styles";

import ActiveFilterChip from "./active-filter-chip.component";

type FilterSearchInputProps = {
  searchString: string;
  searchPlaceholder?: string;
  isEditMode: boolean;
  onChipClick: () => void;
  onChange: (value: string) => void;
  onApply: (value: string) => void;
  onClear: () => void;
  sx?: SxProps;
  type?: "text" | "number";
  className?: string;
  /**
   * Accessible name for the input. It is a bare `Input` with no `<label>`, so
   * without this a screen reader announces only "edit text" and the
   * placeholder is not a substitute. Optional, so existing callers are
   * unaffected.
   */
  ariaLabel?: string;
};

const FilterSearchInput = forwardRef<HTMLInputElement, FilterSearchInputProps>(
  function FilterSearchInput(
    {
      searchString,
      searchPlaceholder = "",
      isEditMode,
      onChipClick,
      onChange,
      onApply,
      onClear,
      sx,
      type,
      className,
      ariaLabel
    }: FilterSearchInputProps,
    ref
  ) {
    const [value, setValue] = useState(searchString);

    useEffect(() => {
      setValue(searchString);
    }, [searchString]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = event.target.value;
      setValue(searchValue);
      onChange(searchValue);
    };

    const handleApplyFilter = () => {
      if (value.trim() !== "") {
        onApply(value);
      } else {
        onClear();
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        handleApplyFilter();
      } else if (event.key === "Escape") {
        onClear();
      }
    };

    if (!isEditMode)
      return (
        <ActiveFilterChip
          label={value}
          onClick={onChipClick}
          onDelete={onClear}
          sx={{ ml: 1 }}
        />
      );

    return (
      <Input
        inputRef={ref}
        data-testid="filter-search-string"
        className={className}
        onBlur={() => handleApplyFilter()}
        placeholder={searchPlaceholder}
        value={value}
        onKeyDown={handleKeyDown}
        onChange={handleInputChange}
        type={type}
        inputProps={ariaLabel ? { "aria-label": ariaLabel } : undefined}
        sx={{
          minWidth: 640,
          ml: 2,
          background: "white",
          borderRadius: "4px",
          ...sx
        }}
      />
    );
  }
);

export default FilterSearchInput;
